import { useEffect, useState } from "react";
import { api } from "../api";
import EmployeePayoutModal from "./EmployeePayoutModal";
import { CURRENCY_SYMBOLS } from "../currency";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

/**
 * Two modes:
 *  - sheetId   → /sheets/{id}/pay-split
 *  - projectId → /projects/{id}/pay-split (focused on realized money)
 */
export default function PaySplitPanel({ sheetId, projectId, reloadKey, title, onChange }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [payoutEmployee, setPayoutEmployee] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    setData(null);
    setErr(null);
    const url = sheetId
      ? `/sheets/${sheetId}/pay-split`
      : projectId
      ? `/projects/${projectId}/pay-split`
      : null;
    if (!url) return;
    api.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [sheetId, projectId, reloadKey, refreshKey]);

  const unmarkPaid = async (userId) => {
    await api.post(`/sheets/${sheetId}/employee-payouts/${userId}/unmark-paid`);
    reload();
    onChange?.();
  };

  const viewProof = async (proofId) => {
    const res = await api.get(
      `/sheets/${sheetId}/payment-proofs/${proofId}/file`,
      { responseType: "blob" }
    );
    window.open(URL.createObjectURL(res.data), "_blank");
  };

  if (err) return <div className="error" style={{ marginTop: 12 }}>{err}</div>;
  if (!data) return null;

  if (projectId) return <ProjectPanel data={data} title={title} />;

  // Sheet mode
  const sym = CURRENCY_SYMBOLS[data.currency] || "$";
  return (
    <div className="pay-split-card">
      <div className="pay-split-head">{title || "Pay split"}</div>
      <EmployeePayoutModal
        open={payoutEmployee !== null}
        sheetId={sheetId}
        employee={payoutEmployee}
        currencySymbol={sym}
        onClose={() => setPayoutEmployee(null)}
        onSaved={() => {
          reload();
          onChange?.();
        }}
      />
      <div className="pay-split-bucket">
        <div className="pay-split-row total">
          <span className="pay-name">Total</span>
          <span className="pay-amount">{sym}{fmt(data.total)}</span>
        </div>
        <div className="pay-split-divider" />
        {data.breakdown.length === 0 && (
          <div className="pay-split-row muted-row">
            <span className="pay-name">No employee pay assigned</span>
            <span className="pay-amount">—</span>
          </div>
        )}
        {data.breakdown.map((r) => {
          const unassigned = Number(r.amount) === 0 && (r.assigned_count ?? 0) === 0;
          return (
            <div key={r.user_id} className={`pay-split-row${unassigned ? " unassigned" : ""}`}>
              <span className="pay-name">
                {r.name}
                {unassigned && <span className="pay-hint"> · not assigned to any task</span>}
                {r.paid && <span className="paid-tag">paid</span>}
              </span>
              <span className="row-h" style={{ gap: 8 }}>
                <span className="pay-amount">{sym}{fmt(r.amount)}</span>
                {r.paid && (
                  <span className="row-h" style={{ gap: 4 }}>
                    <button
                      className="ghost"
                      style={{ padding: "3px 8px", fontSize: "0.72rem" }}
                      onClick={() => viewProof(r.proof_id)}
                    >
                      proof
                    </button>
                    <button
                      className="ghost"
                      style={{ padding: "3px 8px", fontSize: "0.72rem" }}
                      onClick={() => unmarkPaid(r.user_id)}
                    >
                      unmark
                    </button>
                  </span>
                )}
                {!unassigned && !r.paid && (
                  <button
                    className="ghost"
                    style={{ padding: "3px 10px", fontSize: "0.72rem" }}
                    onClick={() => setPayoutEmployee(r)}
                  >
                    Mark paid…
                  </button>
                )}
              </span>
            </div>
          );
        })}
        <div className="pay-split-divider strong" />
        <div className="pay-split-row your">
          <span className="pay-name">Your share</span>
          <span className="pay-amount">{sym}{fmt(data.admin_share)}</span>
        </div>
      </div>
    </div>
  );
}

function ProjectPanel({ data, title }) {
  const buckets = (data.buckets || []).filter(
    (b) => (b.theoretical?.breakdown?.length || 0) > 0 || Number(b.theoretical?.total || 0) > 0
  );
  if (buckets.length === 0) {
    return (
      <div className="pay-split-card">
        <div className="pay-split-head">{title || "Outstanding pay split"}</div>
        <div className="pay-split-empty">Nothing outstanding — all milestones are paid.</div>
      </div>
    );
  }
  return (
    <div className="pay-split-card">
      {buckets.map((b) => {
        const sym = CURRENCY_SYMBOLS[b.currency] || "$";
        const theo = b.theoretical || {};
        return (
          <div key={b.currency} className="pay-split-bucket pay-split-theoretical">
            <div className="pay-split-row total">
              <span className="pay-name">Outstanding from client</span>
              <span className="pay-amount">{sym}{fmt(theo.total)}</span>
            </div>
            <div className="pay-split-divider" />
            {(theo.breakdown || []).map((r) => {
              const unassigned = Number(r.amount) === 0 && (r.assigned_count ?? 0) === 0;
              return (
                <div
                  key={r.user_id}
                  className={`pay-split-row${unassigned ? " unassigned" : ""}`}
                >
                  <span className="pay-name">
                    {r.name}
                    {unassigned && <span className="pay-hint"> · not assigned to any task</span>}
                  </span>
                  <span className="pay-amount">{sym}{fmt(r.amount)}</span>
                </div>
              );
            })}
            <div className="pay-split-divider strong" />
            <div className="pay-split-row your">
              <span className="pay-name">Your share</span>
              <span className="pay-amount">{sym}{fmt(theo.admin_share)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
